// src/data/circleTalkPrompts.js
//
// "Talk about this": questions a Friend Circle takes away to talk about in
// person. There is no answer box anywhere near them, on purpose - the app
// doesn't host the conversation, it hands everyone the same few questions and
// gets out of the way. That's Module 6's point too: the friendship happens on
// the walk to the bus, side by side, not in the rehearsal.
//
// The same rule as the pulse questions: ask about YOURSELF and about the
// ideas in the module, never about anyone in the circle, never ranking or
// comparing people, never "who here...". Questions about people outside the
// circle ask how you handled something, not who they are.
//
// Keyed by module number (friendshipModulesMetadata.js), three a module, and
// written against the modules as they are now (rewritten September 2026).
// They replace the old group-reflection prompts, which were keyed to the
// modules before the rewrite and were typed into the app for the circle to
// read.

export const CIRCLE_TALK_PROMPTS = {
  1: [
    { prompt: "Think of a time a photo or a story you weren't in got to you more than you expected. How long did it take to feel normal again?" },
    { prompt: 'The module says to sleep on a reply to something that stung. Does waiting help you, or does it make it worse?' },
    { prompt: "When something's wrong, would you rather a friend texted or called? Why that one?" },
  ],
  2: [
    { prompt: 'The roles in the module are a lens, not a law. Which one do you slip into without meaning to, and would you choose it?' },
    { prompt: "What's an unwritten rule in a group you're part of that nobody has ever said out loud?" },
    { prompt: 'Have you ever been the one on the edge of a group? What helped, or what would have?' },
  ],
  3: [
    { prompt: 'When you get flooded in an argument, what does it feel like for you, and how long does it take to come back down?' },
    { prompt: 'Do you tend to go quiet or go loud when something is wrong? Where do you think you learnt that?' },
    { prompt: "What's the difference, for you, between an apology that lands and one that doesn't?" },
  ],
  4: [
    { prompt: "Is there something you'd type in a chat that you'd never say to someone's face, or the other way round?" },
    { prompt: "When a pile-on starts in a big chat, what makes it hard to be the one who says 'not this'?" },
    { prompt: 'Read receipts: on or off, and why?' },
  ],
  5: [
    { prompt: 'Have you had a friendship fade without anyone deciding it should? When did you notice?' },
    { prompt: "What helps when you miss someone who's still around, just not close any more?" },
    { prompt: 'If a friend needed more space from you, how would you want them to tell you?' },
  ],
  6: [
    { prompt: 'Where did you meet the people you are closest to now? Was it the activity, or the bit after it?' },
    { prompt: "What's something you notice about how a person treats other people that tells you more than their playlist does?" },
    { prompt: "What's one small move that turns a familiar face into a friend? Have you seen it work?" },
  ],
  7: [
    { prompt: "When have you felt like everyone was looking at you, and found out later nobody noticed?" },
    { prompt: 'What makes no hard for you to say? Which of the scripts in the module would you actually use?' },
    { prompt: "What would it take for you to be the first to say 'nah, not this' in a group?" },
  ],
  8: [
    { prompt: "Is there someone younger on the edge of a group you're in, the way you were once? What's one thing you could do?" },
    { prompt: "Which if-then plan from these modules would you actually keep? What's the 'if'?" },
    { prompt: "Who is someone you've never properly thanked? What would you say if you did?" },
  ],
};

// For a circle that hasn't picked a module yet. Three a week, in turn.
export const CIRCLE_TALK_PROMPTS_GENERAL = [
  { prompt: 'What does a good friend do when you have had a bad day?' },
  { prompt: "What's something small a friend did once that you still remember?" },
  { prompt: "What's easier for you: asking for help, or offering it?" },
  { prompt: "What's a friendship you've had that was right for its time, even if it didn't last?" },
  { prompt: 'How do you know when it is fine to bring something up, and when to leave it?' },
  { prompt: 'What makes a group feel easy to be in, rather than hard work?' },
  { prompt: "When you're new somewhere, what helps you most?" },
  { prompt: "What's something you changed your mind about this year?" },
  { prompt: 'What do you wish people asked you about more?' },
];

/**
 * The questions a circle is carrying right now: its module's, or three
 * general ones that change once a week.
 */
export function talkPromptsFor(moduleId, now = Date.now()) {
  const forModule = moduleId ? CIRCLE_TALK_PROMPTS[moduleId] : null;
  if (forModule?.length) return { moduleId, prompts: forModule };
  const week = Math.floor(now / (7 * 24 * 60 * 60 * 1000));
  const n = CIRCLE_TALK_PROMPTS_GENERAL.length;
  const start = (week * 3) % n;
  return {
    moduleId: null,
    prompts: [0, 1, 2].map((i) => CIRCLE_TALK_PROMPTS_GENERAL[(start + i) % n]),
  };
}
